import { all, dropTask } from 'ember-concurrency';
import { eq } from 'tio-ui/utilities';
import { fn } from '@ember/helper';
import { Header, HStack, VStack } from 'tio-ui/components/layout';
import { LinkTo } from '@ember/routing';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { trackedObject } from '@ember/reactive/collections';
import {
  getAllValuesForTasField,
  copyFieldsAndUpdatePerProgramTemplate,
  getSingleValueForTasField,
  setSingleValueForTasField,
  getConfigForTasField,
  type FieldName,
} from 'tio-common/utils/tuition-assistance/fields';
import { hasInProgressInstanceOfCurrentProgramTemplate } from 'tio-common/utils/tuition-assistance/tas-grouping';
import {
  abandonProgramRequest,
  requestProgramApprovalRequest,
} from 'tio-common/requests/tas-program-instance';
import { buildTasEmployeeActor } from 'tio-common/utils/tas-actor';
import { getInputmaskAlias, getInputmaskUnmaskedValue } from 'tio-common/utils/inputmask';
import { normalizeDate } from 'tio-common/utils/date/normalize-date';
import { formatDateToISO } from 'tio-common/utils/date';
import Button from 'tio-ui/components/button';
import Component from '@glimmer/component';
import Footer from 'tio-ui/components/footer';
import Modal from 'tio-ui/components/modal';
import ApplicationHistoryTimeline from 'tio-common/components/tas/application-history-timeline';
import { PhCaretLeft, PhTrash } from 'ember-phosphor-icons';
import ProgressTracker from 'tio-employee/components/tas/applications/guided/progress-tracker';
import CompletedStepSummary from 'tio-employee/components/tas/applications/guided/completed-step-summary';
import StepProgramDetails from './step-program-details';
import StepAttachments from './step-attachments';
import StepReviewSubmit from './step-review-submit';
import type { CustomFieldSignature } from 'tio-common/types/tuition-assistance';
import type { TASProgramInstanceModelFieldsSignature } from 'tio-data/models/tas-program-instance';
import type { TASProgramTemplateModelFieldsSignature } from 'tio-data/models/tas-program-template';
import type AgreementModel from 'tio-data/models/agreement';
import type ConditionModel from 'tio-data/models/condition';
import type IntlService from 'ember-intl/services/intl';
import type LpnPage from 'tio-data/models/lpn-page';
import type Owner from '@ember/owner';
import type PartnerService from 'tio-employee/services/partner';
import type RouterService from '@ember/routing/router-service';
import type SessionContextService from 'tio-employee/services/session-context';
import type StoreService from 'ember-data/store';
import type TasGroupModel from 'tio-data/models/tas-group';
import type TASProgramInstanceModel from 'tio-data/models/tas-program-instance';
import type TasUnifiedInstitutionModel from 'tio-data/models/tas-unified-institution';
import type { TasUnifiedInstitution } from 'tio-data/models/search';

type ProgramInstanceFieldName = keyof TASProgramTemplateModelFieldsSignature;

export type FormField = {
  name: ProgramInstanceFieldName;
  mask: string | null;
  type: string | null;
  rules: {
    type: string;
    typeError?: string;
  };
  disabled: boolean;
};

export interface StepDefinition {
  id: string;
  label: string;
  isApplicable: boolean;
  isComplete: boolean;
  summaryText: string;
}

export interface ProgramGuidedWizardSignature {
  Args: {
    model: {
      activeInstances: TASProgramInstanceModel[];
      programInstance: TASProgramInstanceModel;
      selectedSchool?: TasUnifiedInstitutionModel;
      tasGroups: TasGroupModel[];
    };
  };
}

const ATTACHMENT_CLASSIFICATION = 'PROGRAM_APPROVAL_DOCUMENTS';

export default class ProgramGuidedWizard extends Component<ProgramGuidedWizardSignature> {
  programFieldsCopy!: TASProgramInstanceModelFieldsSignature;

  constructor(owner: Owner, args: ProgramGuidedWizardSignature['Args']) {
    super(owner, args);
    const instance = args.model.programInstance;
    this.programFieldsCopy = trackedObject(instance.fields);
    const programTemplateCustomFields = instance.tasProgramTemplate?.instanceCustomFields || [];
    this.customFieldsCopy = copyFieldsAndUpdatePerProgramTemplate(
      instance.customFields,
      programTemplateCustomFields
    );
    this.setSelectedSchoolIfExists();
    this.updateCustomRequiredFields(this.customFieldsCopy);

    const initialStep = this.computeInitialStep();
    this.currentStep = initialStep;
    this.furthestVisitedStepIndex = initialStep;
  }

  // --- Tracked properties ---
  @tracked currentStep = 0;
  @tracked furthestVisitedStepIndex = 0;
  @tracked attemptedSteps = new Set<number>();
  @tracked acceptedConditions: ConditionModel[] = [];
  @tracked customFieldsCopy: CustomFieldSignature[] = [];
  @tracked selectedSchool?: TasUnifiedInstitutionModel;
  @tracked isLpnSchoolSelected = false;
  @tracked requiredCustomFields = false;
  @tracked hasSubmitted = false;
  @tracked submitError: string | null = null;
  @tracked isDeleteProgramOpen = false;

  @service declare router: RouterService;
  @service declare sessionContext: SessionContextService;
  @service declare store: StoreService;
  @service declare partner: PartnerService;
  @service declare intl: IntlService;

  // --- Core getters ---
  get instance() {
    return this.args.model.programInstance;
  }

  get programTemplate() {
    return this.args.model.programInstance.tasProgramTemplate;
  }

  get attachmentClassification() {
    return ATTACHMENT_CLASSIFICATION;
  }

  get programDisplayName() {
    return this.getSingleValueForField('NAME') || '';
  }

  get hasMeaningfulHistory() {
    const history = this.instance.fullHistory;
    if (!history?.length) return false;
    // Don't show if the only entry is the initial CREATED event
    return history.some(
      (item) =>
        item.logType !== 'state_transition' ||
        (item as { stateTransitionType?: string }).stateTransitionType !== 'CREATED'
    );
  }

  get programFormFields(): FormField[] {
    const numberTypeError = 'Value must be a number';
    return [
      { name: 'NAME', mask: null, type: null, rules: { type: 'string' }, disabled: true },
      { name: 'EMPLOYEE_ID', mask: null, type: null, rules: { type: 'string' }, disabled: true },
      { name: 'FT_PT_OTHER', mask: null, type: null, rules: { type: 'string' }, disabled: true },
      {
        name: 'EMPLOYEE_DOB',
        mask: null,
        type: 'date',
        rules: { type: 'string' },
        disabled: false,
      },
      {
        name: 'EMPLOYEE_EMAIL',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
      },
      {
        name: 'SCHOOL_INSTITUTION_NAME',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
      },
      { name: 'STUDENT_ID', mask: null, type: null, rules: { type: 'string' }, disabled: false },
      {
        name: 'PROGRAM_SPECIALIZATION',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
      },
      { name: 'PROGRAM_MAJOR', mask: null, type: null, rules: { type: 'string' }, disabled: false },
      {
        name: 'ESTIMATED_COST',
        mask: 'currency',
        type: 'number',
        rules: { type: 'number', typeError: numberTypeError },
        disabled: false,
      },
      {
        name: 'ENROLLMENT_OBJECTIVE',
        mask: null,
        type: null,
        rules: { type: 'string' },
        disabled: false,
      },
      {
        name: 'ESTIMATED_PROGRAM_BEGIN',
        mask: null,
        type: 'date',
        rules: { type: 'date' },
        disabled: false,
      },
      {
        name: 'ESTIMATED_PROGRAM_COMPLETION',
        mask: null,
        type: 'date',
        rules: { type: 'date' },
        disabled: false,
      },
    ];
  }

  // --- Step management ---
  get allStepDefinitions(): StepDefinition[] {
    const assetCount = this.instance.tasAssets?.length || 0;
    const schoolName = this.getSingleValueForField('SCHOOL_INSTITUTION_NAME');
    return [
      {
        id: 'program-details',
        label: this.intl.t('tas.guided.steps.program_details'),
        isApplicable: true,
        isComplete: this.isProgramDetailsComplete,
        summaryText: schoolName || '',
      },
      {
        id: 'attachments',
        label: this.intl.t('common.attachments'),
        isApplicable: this.shouldShowAttachments,
        isComplete: !this.isAttachmentRequired || this.hasRequiredAttachments,
        summaryText: assetCount
          ? (this.intl.t('tas.guided.steps.files_included', { count: assetCount }) as string)
          : '',
      },
      {
        id: 'submit',
        label: this.intl.t('tas.guided.steps.submit'),
        isApplicable: true,
        isComplete: false,
        summaryText: '',
      },
    ];
  }

  get applicableSteps() {
    return this.allStepDefinitions.filter((s) => s.isApplicable);
  }

  get currentStepDef() {
    return this.applicableSteps[this.currentStep];
  }

  computeInitialStep() {
    const steps = this.applicableSteps;
    for (let i = 0; i < steps.length - 1; i++) {
      if (!steps[i]!.isComplete) {
        return i;
      }
    }
    return Math.max(0, steps.length - 1);
  }

  isStepPassed = (index: number) => {
    return index < this.currentStep;
  };

  hasStepBeenAttempted = (index: number) => {
    return this.attemptedSteps.has(index);
  };

  advanceStep = () => {
    const updated = new Set(this.attemptedSteps);
    updated.add(this.currentStep);
    this.attemptedSteps = updated;
    this.saveTask.perform();
    const nextStep = Math.min(this.currentStep + 1, this.applicableSteps.length - 1);
    this.currentStep = nextStep;
    this.furthestVisitedStepIndex = Math.max(this.furthestVisitedStepIndex, nextStep);
  };

  canGoToStep = (stepIndex: number) => {
    return (
      stepIndex >= 0 &&
      stepIndex < this.applicableSteps.length &&
      stepIndex <= this.furthestVisitedStepIndex
    );
  };

  goToStep = (stepIndex: number) => {
    if (this.canGoToStep(stepIndex)) {
      this.currentStep = stepIndex;
    }
  };

  isReopenableFutureStep = (index: number) => {
    return index > this.currentStep && this.canGoToStep(index);
  };

  stepDisplayNumber = (index: number) => {
    return index + 1;
  };

  // --- Completion + validation ---
  get isProgramDetailsComplete() {
    return this.isFormValid && this.requiredCustomFields;
  }

  get isFormValid() {
    const fieldValues = [];
    for (const field of this.programFormFields) {
      const config = getConfigForTasField(field.name, this.programTemplate.fields);
      if (config.required) {
        const value = getSingleValueForTasField(field.name as FieldName, this.programFieldsCopy);
        fieldValues.push(value);
      }
    }
    return fieldValues.every((value) => value !== null && value !== undefined && value !== '');
  }

  get customFieldValidationErrors() {
    const errors: { errors: string[]; value: string; path: string }[] = [];
    for (const field of this.customFieldsCopy) {
      if (field.required) {
        const val = field.values[0];
        if (val === null || val === undefined || val === '') {
          errors.push({ path: field.label, errors: [`${field.label} is required`], value: '' });
        }
      }
    }
    return errors;
  }

  // --- Attachments ---
  get shouldShowAttachments() {
    return (
      this.programTemplate.attachmentsForProgramApprovalVisible ||
      this.instance.state === 'TAS.ProgramInstanceState.PRIMED'
    );
  }

  get isAttachmentRequired() {
    if (!this.shouldShowAttachments) return false;
    return this.programTemplate.requireAttachmentsForProgramApproval;
  }

  get hasRequiredAttachments() {
    if (!this.isAttachmentRequired) return true;
    return !!this.instance.tasAssets?.length;
  }

  get attachmentsListItems() {
    return getAllValuesForTasField(
      'PROGRAM_APPROVAL_DOCUMENTS_ATTACHMENT_FIELD',
      this.programTemplate.fields
    ) as string[];
  }

  get attachmentsListInstructions() {
    return getSingleValueForTasField(
      'PROGRAM_APPROVAL_DOCUMENTS_STRING',
      this.programTemplate.fields
    ) as string;
  }

  // --- Conditions ---
  get conditionsForSubmit() {
    return this.programTemplate?.programApproveConditions || [];
  }

  get hasAgreedAllConditions() {
    if (!this.conditionsForSubmit.length) {
      return true;
    }
    return this.conditionsForSubmit.length === this.acceptedConditions.length;
  }

  // --- Participant / eligibility ---
  get isActiveParticipant() {
    return this.instance.employee?.tasParticipant?.isActive || false;
  }

  get isRestrictedByProgramExclusivity() {
    if (this.args.model.tasGroups.length === 0) {
      return false;
    }
    return hasInProgressInstanceOfCurrentProgramTemplate(
      this.args.model.tasGroups,
      this.programTemplate.id as string,
      this.args.model.activeInstances
    );
  }

  get canSubmitApplication() {
    return (
      this.isActiveParticipant &&
      this.instance.state === 'TAS.ProgramInstanceState.DRAFT' &&
      this.hasAgreedAllConditions &&
      this.hasRequiredAttachments &&
      this.isFormValid &&
      this.requiredCustomFields &&
      !this.isRestrictedByProgramExclusivity
    );
  }

  // --- Custom fields ---
  get customEmployeeFields(): CustomFieldSignature[] {
    return this.customFieldsCopy.filter((field) => field.province === 'EMPLOYEE');
  }

  get customProgramFields(): CustomFieldSignature[] {
    return this.customFieldsCopy.filter((field) => field.province === 'PROGRAM');
  }

  // --- LPN ---
  get isLpnEnabled() {
    const { lpn } = this.partner.companySettings;
    if (lpn && 'isEnabled' in lpn) {
      return lpn.isEnabled as boolean;
    }
    return false;
  }

  private hasValidDapipId(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { dapipId: string } {
    return !!(institution && 'dapipId' in institution && institution.dapipId);
  }

  private hasValidInstitutionId(
    institution: TasUnifiedInstitution | undefined
  ): institution is TasUnifiedInstitution & { institutionId: string } {
    return !!(institution && 'institutionId' in institution && institution.institutionId);
  }

  isSelectedSchoolLpn = async (searchResult: TasUnifiedInstitution) => {
    const lpnSettings = this.partner.companySettings.lpn;
    const isEnabled = lpnSettings?.isEnabled;
    if (!isEnabled) {
      this.isLpnSchoolSelected = false;
      return false;
    }

    let institutionIdentifier: string | undefined;
    if (this.hasValidDapipId(searchResult)) {
      institutionIdentifier = `dapip_${searchResult.dapipId}`;
    } else if (this.hasValidInstitutionId(searchResult)) {
      institutionIdentifier = `tas_${searchResult.institutionId}`;
    } else {
      this.isLpnSchoolSelected = false;
      return false;
    }

    const pages = await this.store.query<LpnPage>('lpn-page', {
      filter: { 'institution-identifier': institutionIdentifier },
      include: 'company-lpn-settings,company-lpn-settings.company-setting',
    });
    if (pages.length === 0) {
      this.isLpnSchoolSelected = false;
      return false;
    }
    const setting = await pages[0]?.settingForCompany(this.partner.companySettings.id);
    const enabled = !setting || setting.isEnabled;
    this.isLpnSchoolSelected = enabled;
    return enabled;
  };

  // --- Field setters ---
  setSelectedSchoolIfExists() {
    if (this.args.model.selectedSchool) {
      this.selectedSchool = this.args.model.selectedSchool;
    }
  }

  getSingleValueForField = (fieldName: keyof TASProgramTemplateModelFieldsSignature) => {
    return getSingleValueForTasField(fieldName as FieldName, this.programFieldsCopy) as string;
  };

  updateValueForField = (
    fieldName: FieldName,
    programField: FormField,
    value: string[] | string | number,
    event?: Event
  ) => {
    let valueToSet = Array.isArray(value) ? value[0] : value;
    if (getInputmaskAlias(event) === 'currency') {
      valueToSet = Number(getInputmaskUnmaskedValue(event)) * 100;
    }
    if (programField.type === 'number') {
      valueToSet = Math.round(Number(valueToSet));
    }
    if (programField.type === 'date' && typeof valueToSet === 'string') {
      valueToSet = normalizeDate(valueToSet as string);
    }
    this.setInstanceField(fieldName, valueToSet);
  };

  updateDateFieldValue = (fieldName: FieldName, selectedDates: Date[]) => {
    if (selectedDates.length > 0 && selectedDates[0]) {
      const isoDate = formatDateToISO(selectedDates[0]);
      this.setInstanceField(fieldName, isoDate);
    }
  };

  // setSingleValueForTasField has per-fields-type overloads that don't narrow
  // when invoked with a generic FieldName. We confine the cast to one helper.
  private setInstanceField = (fieldName: FieldName, value: unknown) => {
    const setField = setSingleValueForTasField as unknown as (
      fieldName: FieldName,
      value: unknown,
      fields: TASProgramInstanceModelFieldsSignature
    ) => void;
    setField(fieldName, value, this.programFieldsCopy);
  };

  didSelectSchool = async (searchResult?: TasUnifiedInstitution) => {
    this.selectedSchool = searchResult as unknown as TasUnifiedInstitutionModel;
    if (!searchResult) {
      this.isLpnSchoolSelected = false;
      return;
    }
    await this.isSelectedSchoolLpn(searchResult);
    setSingleValueForTasField(
      'SCHOOL_INSTITUTION_NAME',
      searchResult?.locationName,
      this.programFieldsCopy
    );
    setSingleValueForTasField(
      'TAS_INSTITUTION_ID',
      searchResult?.institutionId,
      this.programFieldsCopy
    );
    setSingleValueForTasField(
      'DAPIP_INSTITUTION_ID',
      searchResult?.dapipId,
      this.programFieldsCopy
    );
  };

  didUpdateAcceptedConditions = (conditions: ConditionModel[] = []) => {
    this.acceptedConditions = conditions;
  };

  updateCustomRequiredFields = (customFieldsCopy: CustomFieldSignature[]) => {
    this.requiredCustomFields = customFieldsCopy.every((field) => {
      if (field.required) {
        if (field.values.length === 0) {
          return false;
        }
        return field.values.every((value) => value !== null && value !== undefined && value !== '');
      }
      return true;
    });
  };

  updateFieldsWithNewValues = (
    existingFields: CustomFieldSignature[],
    updatedFields: CustomFieldSignature[]
  ): CustomFieldSignature[] => {
    updatedFields.forEach((newObject) => {
      const index = existingFields.findIndex(
        (item) => item.label === newObject.label && item.province === newObject.province
      );
      existingFields.splice(index, 1, newObject);
    });
    return existingFields;
  };

  didUpdateEmployeeCustomFields = (customFields: CustomFieldSignature[]) => {
    const updatedFields = this.updateFieldsWithNewValues(this.customEmployeeFields, customFields);
    this.customFieldsCopy = this.updateFieldsWithNewValues(this.customFieldsCopy, updatedFields);
    this.updateCustomRequiredFields(this.customFieldsCopy);
  };

  didUpdateProgramCustomFields = (customFields: CustomFieldSignature[]) => {
    const updatedFields = this.updateFieldsWithNewValues(this.customProgramFields, customFields);
    this.customFieldsCopy = this.updateFieldsWithNewValues(this.customFieldsCopy, updatedFields);
    this.updateCustomRequiredFields(this.customFieldsCopy);
  };

  // --- Delete ---
  toggleDeleteProgram = () => {
    this.isDeleteProgramOpen = !this.isDeleteProgramOpen;
  };

  // --- Tasks ---
  saveTask = dropTask(async () => {
    try {
      this.instance.fields = this.programFieldsCopy;
      this.instance.customFields = this.customFieldsCopy;
      await this.instance.save();
    } catch (error) {
      console.error('Failed to save program instance:', error);
    }
  });

  saveAgreementsForConditions = dropTask(async () => {
    const user = this.sessionContext.user;
    const agreements = this.acceptedConditions.map((condition) => {
      return this.store.createRecord<AgreementModel>('agreement', { user, condition });
    });
    return await all(agreements.map((agreement) => agreement.save()));
  });

  submitTask = dropTask(async () => {
    if (
      !this.isFormValid ||
      !this.hasAgreedAllConditions ||
      this.isRestrictedByProgramExclusivity
    ) {
      return;
    }
    try {
      this.hasSubmitted = true;
      this.submitError = null;
      await this.saveAgreementsForConditions.perform();
      await this.saveTask.perform();
      await this.store.request(
        requestProgramApprovalRequest(
          this.instance.id as string,
          buildTasEmployeeActor(this.sessionContext.user, this.sessionContext.currentRole)
        )
      );
      await this.instance.reload();
      this.router.transitionTo('authenticated.tas.programs.show', this.instance.id);
    } catch (e) {
      console.error(e);
      this.submitError = e instanceof Error ? e.message : String(e);
    }
  });

  deleteProgram = dropTask(async () => {
    try {
      await this.store.request(
        abandonProgramRequest(
          this.instance.id as string,
          buildTasEmployeeActor(this.sessionContext.user, this.sessionContext.currentRole)
        )
      );
      this.router.transitionTo('authenticated.tas.dashboard');
    } catch (e) {
      console.error(e);
    }
  });

  switchToStandardFlow = dropTask(async () => {
    await this.saveTask.perform();
    this.router.transitionTo('authenticated.tas.programs.new');
  });

  <template>
    <VStack>
      <Header class="tio-row-between">
        <LinkTo class="tio-row-loose text-sm" @route="authenticated.tas.dashboard">
          <PhCaretLeft @size="16" />
          {{t "dashboard"}}
        </LinkTo>
        <div class="tio-row-tight">
          <button
            type="button"
            class="text-xs tio-fg-muted underline hover:tio-fg-secondary cursor-pointer"
            data-test-id="guided-wizard-toggle"
            disabled={{this.switchToStandardFlow.isRunning}}
            {{on "click" this.switchToStandardFlow.perform}}
          >
            {{t "tas.guided.prefer_standard_view"}}
          </button>
          <Button
            data-test-save-application
            @intent="primary"
            @appearance="outlined"
            @isRunning={{this.saveTask.isRunning}}
            {{on "click" this.saveTask.perform}}
          >
            {{t "common.save"}}
          </Button>
          <Button
            @appearance="minimal"
            @intent="danger"
            class="mb-2 text-danger"
            data-test-delete-application
            {{on "click" this.toggleDeleteProgram}}
          >
            <PhTrash @size="32" />
          </Button>
        </div>
      </Header>

      <div class="mb-4 px-1">
        <h2 class="tio-text-xl-semibold">{{t "tas.guided.program_title"}}</h2>
        <p class="text-sm tio-fg-muted">{{t "tas.guided.program_description"}}</p>
      </div>

      <HStack class="items-start gap-6">
        <VStack class="w-full lg:basis-2/3">
          {{#each this.applicableSteps key="id" as |step index|}}
            {{#if (this.isStepPassed index)}}
              <CompletedStepSummary
                @stepNumber={{index}}
                @label={{step.label}}
                @summary={{step.summaryText}}
                @isComplete={{step.isComplete}}
                @onEdit={{fn this.goToStep index}}
              />
            {{else if (eq index this.currentStep)}}
              {{#if (eq step.id "program-details")}}
                <StepProgramDetails
                  @stepNumber={{index}}
                  @programTemplate={{this.programTemplate}}
                  @instanceFields={{this.programFieldsCopy}}
                  @selectedSchool={{this.selectedSchool}}
                  @isActiveParticipant={{this.isActiveParticipant}}
                  @isLpnSchoolSelected={{this.isLpnSchoolSelected}}
                  @programFormFields={{this.programFormFields}}
                  @customEmployeeFields={{this.customEmployeeFields}}
                  @customProgramFields={{this.customProgramFields}}
                  @customFieldValidationErrors={{this.customFieldValidationErrors}}
                  @hasAttempted={{this.hasStepBeenAttempted index}}
                  @onSelectSchool={{this.didSelectSchool}}
                  @onUpdateValueForField={{this.updateValueForField}}
                  @onUpdateDateFieldValue={{this.updateDateFieldValue}}
                  @getSingleValueForField={{this.getSingleValueForField}}
                  @onDidUpdateEmployeeCustomFields={{this.didUpdateEmployeeCustomFields}}
                  @onDidUpdateProgramCustomFields={{this.didUpdateProgramCustomFields}}
                  @onNext={{this.advanceStep}}
                />
              {{else if (eq step.id "attachments")}}
                <StepAttachments
                  @stepNumber={{index}}
                  @programInstance={{this.instance}}
                  @programTemplate={{this.programTemplate}}
                  @attachmentClassificationString={{this.attachmentClassification}}
                  @attachmentsListInstructions={{this.attachmentsListInstructions}}
                  @attachmentsListItems={{this.attachmentsListItems}}
                  @isAttachmentRequired={{this.isAttachmentRequired}}
                  @hasRequiredAttachments={{this.hasRequiredAttachments}}
                  @onNext={{this.advanceStep}}
                  @hasAttempted={{this.hasStepBeenAttempted index}}
                  @isComplete={{step.isComplete}}
                />
              {{else if (eq step.id "submit")}}
                <StepReviewSubmit
                  @stepNumber={{index}}
                  @conditionsForSubmit={{this.conditionsForSubmit}}
                  @acceptedConditions={{this.acceptedConditions}}
                  @canSubmitApplication={{this.canSubmitApplication}}
                  @isSubmitting={{this.submitTask.isRunning}}
                  @submitError={{this.submitError}}
                  @completedSteps={{this.applicableSteps}}
                  @currentStep={{this.currentStep}}
                  @onDidUpdateAcceptedConditions={{this.didUpdateAcceptedConditions}}
                  @onSubmit={{this.submitTask.perform}}
                  @onEditStep={{this.goToStep}}
                />
              {{/if}}
            {{else}}
              {{#if (this.isReopenableFutureStep index)}}
                <button
                  type="button"
                  class="w-full border tio-border-default rounded-lg py-4 px-5 tio-row text-left hover:tio-border-brand hover:bg-violet-50/40 transition-colors"
                  {{on "click" (fn this.goToStep index)}}
                >
                  <span
                    class="tio-inline-row-center w-7 h-7 rounded-full border-2 tio-border-brand tio-text-sm-medium tio-fg-brand tio-bg-surface"
                  >{{this.stepDisplayNumber index}}</span>
                  <span class="font-medium tio-fg-secondary">{{step.label}}</span>
                </button>
              {{else}}
                <div class="border tio-border-default rounded-lg py-4 px-5 opacity-50 tio-row">
                  <span
                    class="tio-inline-row-center w-7 h-7 rounded-full border-2 tio-border-default tio-text-sm-medium tio-fg-disabled"
                  >{{this.stepDisplayNumber index}}</span>
                  <span class="tio-fg-disabled font-medium">{{step.label}}</span>
                </div>
              {{/if}}
            {{/if}}
          {{/each}}
        </VStack>

        <div class="hidden lg:block w-full lg:basis-1/3">
          <div class="sticky top-4">
            <ProgressTracker
              @steps={{this.applicableSteps}}
              @currentStepIndex={{this.currentStep}}
              @furthestVisitedStepIndex={{this.furthestVisitedStepIndex}}
              @onGoToStep={{this.goToStep}}
            />
          </div>
        </div>
      </HStack>

      {{#if this.hasMeaningfulHistory}}
        <div class="w-full lg:w-2/3 lg:pr-3">
          <ApplicationHistoryTimeline
            @history={{this.instance.fullHistory}}
            @title={{this.programDisplayName}}
            @translationPrefix="tuition_assistance.program_instances.history"
          />
        </div>
      {{/if}}
    </VStack>

    <Modal
      @isOpen={{this.isDeleteProgramOpen}}
      @onClose={{this.toggleDeleteProgram}}
      data-test-delete-program-modal
    >
      <:header>{{t "tas.program.delete_program"}}</:header>
      <:body>
        <p>{{t "tas.program.confirm_program_delete"}}</p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{this.deleteProgram.isRunning}}
          @onCancel={{this.toggleDeleteProgram}}
          @onSubmit={{this.deleteProgram.perform}}
        />
      </:footer>
    </Modal>
  </template>
}
